import { useGetIdentity } from "react-admin";
import { useMatch } from "react-router-dom";

import { UserType } from "../generated/graphql";
import { CUSTOMER_SERVICE_ORGANIZATION_ROUTE } from "../lib/customerServiceOrganizationRoutes";
import { useCustomerServiceLearningOrganizations } from "./useCustomerServiceLearningOrganizations";

/**
 * Hook to get the current organization context.
 *
 * For managers: Returns their organization ID from identity (each manager belongs to exactly one organization).
 * For customer service: Returns the open organization page's organization (from the URL) when it is a known organization, otherwise null.
 *
 * @returns Object with organizationId and isLoading state
 */
export const useOrganizationContext = (): { organizationId: string | null; isLoading: boolean } => {
  const { data: identity, isLoading } = useGetIdentity();
  const isCustomerService = identity?.kind === UserType.CustomerService;
  const routeOrganizationId = useMatch(`${CUSTOMER_SERVICE_ORGANIZATION_ROUTE}/*`)?.params.organizationId;
  const { organizations, isLoading: areOrganizationsLoading } = useCustomerServiceLearningOrganizations({
    skip: !isCustomerService || !routeOrganizationId,
  });

  if (isCustomerService) {
    const isKnownOrganization = organizations.some(organization => organization.id === routeOrganizationId);
    return {
      organizationId: isKnownOrganization && routeOrganizationId ? routeOrganizationId : null,
      isLoading: areOrganizationsLoading,
    };
  }

  // Managers have their organization ID directly in identity
  return { organizationId: identity?.organizationId || null, isLoading };
};
